import { useEffect, useRef, useState } from 'react'
import type { EarnedMilestone } from '@/utils/milestones'
import { TIER_COLORS, TIER_LABELS } from '@/utils/milestones'

interface CelebrationModalProps {
  milestone: EarnedMilestone | null
  onClose: () => void
}

// Confetti particle
interface Particle {
  x: number
  y: number
  vx: number
  vy: number
  color: string
  size: number
  rotation: number
  rotationSpeed: number
  opacity: number
}

export default function CelebrationModal({ milestone, onClose }: CelebrationModalProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const animRef = useRef<number>(0)
  const [showContent, setShowContent] = useState(false)

  useEffect(() => {
    if (!milestone) return
    // Delay content reveal for dramatic effect
    const timer = setTimeout(() => setShowContent(true), 400)
    return () => { clearTimeout(timer); setShowContent(false) }
  }, [milestone])

  // Confetti animation
  useEffect(() => {
    if (!milestone) return
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    canvas.width = window.innerWidth
    canvas.height = window.innerHeight

    const tierColor = TIER_COLORS[milestone.tier]
    const colors = [tierColor, '#2dd4bf', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#22c55e', '#f8fafc']
    const particles: Particle[] = []

    // Create burst of particles
    for (let i = 0; i < 150; i++) {
      const angle = (Math.PI * 2 * i) / 150 + (Math.random() - 0.5)
      const speed = 3 + Math.random() * 8
      particles.push({
        x: canvas.width / 2,
        y: canvas.height / 2 - 50,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed - 2,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 4 + Math.random() * 8,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.2,
        opacity: 1,
      })
    }

    // Side cannons
    for (let i = 0; i < 60; i++) {
      const fromLeft = Math.random() > 0.5
      particles.push({
        x: fromLeft ? 0 : canvas.width,
        y: canvas.height * 0.6 + Math.random() * canvas.height * 0.2,
        vx: (fromLeft ? 1 : -1) * (2 + Math.random() * 6),
        vy: -(4 + Math.random() * 8),
        color: colors[Math.floor(Math.random() * colors.length)],
        size: 3 + Math.random() * 6,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.3,
        opacity: 1,
      })
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height)

      let active = false
      for (const p of particles) {
        if (p.opacity <= 0) continue
        active = true

        p.x += p.vx
        p.vy += 0.15 // gravity
        p.y += p.vy
        p.rotation += p.rotationSpeed
        p.opacity -= 0.0015
        p.vx *= 0.99

        ctx.save()
        ctx.translate(p.x, p.y)
        ctx.rotate(p.rotation)
        ctx.globalAlpha = Math.max(0, p.opacity)
        ctx.fillStyle = p.color
        // Mix of rectangles and circles for variety
        if (p.size > 6) {
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2)
        } else {
          ctx.beginPath()
          ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2)
          ctx.fill()
        }
        ctx.restore()
      }

      if (active) {
        animRef.current = requestAnimationFrame(animate)
      }
    }

    const START_DELAY = 1100 // wait for card to finish transitioning in
    const startTimer = setTimeout(() => {
      animRef.current = requestAnimationFrame(animate)
    }, START_DELAY)

    // Secondary burst waves for sustained celebration
    const wave = (delay: number) => setTimeout(() => {
      for (let i = 0; i < 80; i++) {
        const angle = (Math.PI * 2 * i) / 80 + (Math.random() - 0.5)
        const speed = 3 + Math.random() * 7
        particles.push({
          x: canvas.width / 2,
          y: canvas.height / 2 - 50,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - 2,
          color: colors[Math.floor(Math.random() * colors.length)],
          size: 4 + Math.random() * 8,
          rotation: Math.random() * Math.PI * 2,
          rotationSpeed: (Math.random() - 0.5) * 0.2,
          opacity: 1,
        })
      }
      // Restart animation loop if it stopped
      cancelAnimationFrame(animRef.current)
      animRef.current = requestAnimationFrame(animate)
    }, delay)
    const timers = [wave(1100 + 800), wave(1100 + 1600), wave(1100 + 2400)]

    return () => {
      cancelAnimationFrame(animRef.current)
      clearTimeout(startTimer)
      timers.forEach(clearTimeout)
    }
  }, [milestone])

  if (!milestone) return null

  const tierColor = TIER_COLORS[milestone.tier]
  const tierLabel = `${TIER_LABELS[milestone.tier]} ${milestone.tierLevel}`

  return (
    <div className="fixed inset-0 z-[100]" onClick={onClose}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70" />

      {/* Confetti canvas (above backdrop) */}
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none z-10" />

      {/* Content */}
      <div
        className={`absolute inset-0 z-20 flex items-center justify-center p-4 transition-all duration-700 ${
          showContent ? 'opacity-100 scale-100' : 'opacity-0 scale-75'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-[var(--bg-secondary)] rounded-2xl p-6 md:p-8 max-w-sm w-full text-center shadow-2xl border border-[var(--border)]">
          {/* Badge */}
          <div
            className="w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center text-5xl shadow-lg"
            style={{
              background: `radial-gradient(circle at 30% 30%, ${tierColor}40, ${tierColor}15)`,
              border: `3px solid ${tierColor}`,
            }}
          >
            {milestone.icon}
          </div>

          {/* Tier label */}
          <p
            className="text-xs font-bold uppercase tracking-widest mb-1"
            style={{ color: tierColor }}
          >
            {tierLabel}
          </p>

          {/* Milestone name */}
          <h2 className="text-2xl font-bold text-[var(--text-primary)] mb-4">
            {milestone.name}
          </h2>

          {/* Health fact */}
          <div className="bg-[var(--bg-primary)] rounded-lg p-4 mb-6">
            <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
              {milestone.healthFact}
            </p>
          </div>

          {/* Close button */}
          <button
            onClick={onClose}
            className="btn btn-primary w-full"
          >
            Keep Going
          </button>
        </div>
      </div>
    </div>
  )
}
